import { FormEvent, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Flex,
  mainTheme,
  Modal,
  ModalContext,
  Image,
  Text,
  Input,
  useFormValidation,
  useFormState,
  FormErrorTypes,
  Form,
} from 'dash-ui';

import {
  GetAmenitiesDocument,
  GetAmenitiesForManageDocument,
  useSetAmenityNameInPropertyMutation,
} from 'gql/generated.graphql';
import { PropertyFeaturesAndAmenityTabModalsEnum } from 'containers/properties/PropertyFeaturesAndAmenities/constants';
import { DashValidation, isEmptyOrNil, isNameLengthValid } from 'dash-common';

const AmenityFlexItem = styled(Flex)`
  border: 1px solid ${mainTheme.colors.n30};
  border-radius: 8px;
`;

const AmenityImage = styled(Image)`
  border: 1px solid ${mainTheme.colors.n30};
`;

enum FormFields {
  Name = 'name',
}

const FormFieldsList = [FormFields.Name];

function EditAmenityNameInPropertyModal() {
  const { t: tPropertyPage } = useTranslation('pages', {
    keyPrefix: 'propertyPage',
  });
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });
  const { t: tFormMessages } = useTranslation('forms', {
    keyPrefix: 'messages',
  });

  const { isOpenCurrent, toggleModal, payload } = useContext(ModalContext);
  const isOpen =
    !!isOpenCurrent[
      PropertyFeaturesAndAmenityTabModalsEnum
        .EDIT_AMENITY_NAME_IN_PROPERTY_MODAL
    ];

  const [formErrors, , [setNameError], [resetFormErrors]] =
    useFormValidation(FormFieldsList);
  const [
    formState,
    [setName],
    [formOnSubmit, formOnError, formOnSuccess, , resetForm],
  ] = useFormState(FormFieldsList);

  const formRef = useRef<HTMLFormElement | null>(null);

  const [setAmenityNameInPropertyMutation, { loading }] =
    useSetAmenityNameInPropertyMutation();

  const validateName = (value: string) => {
    if (isEmptyOrNil(value)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: tFormMessages`nameBlank`,
      });
      return false;
    }
    if (!isNameLengthValid(value)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: tFormMessages('maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
      return false;
    }
    setNameError(null);

    return true;
  };

  const onNameTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    validateName(e.target.value);
  };

  const onSubmitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { name } = formState;
    if (!validateName(name)) {
      return null;
    }

    setAmenityNameInPropertyMutation({
      variables: {
        propertyId: payload?.property?._id,
        amenityId: payload?.amenity?._id,
        name,
        isPrimary: payload?.amenity?.isPrimary,
      },
      refetchQueries: [GetAmenitiesDocument, GetAmenitiesForManageDocument],
    })
      .then(() => {
        formOnSuccess();
        onCloseModal();
      })
      .catch(() => {
        formOnError();
        onCloseModal();
      });
  };

  const onCloseModal = () => {
    toggleModal(
      false,
      PropertyFeaturesAndAmenityTabModalsEnum.EDIT_AMENITY_NAME_IN_PROPERTY_MODAL
    );
    if (payload.nextModalToOpen) {
      toggleModal(true, payload.nextModalToOpen);
    }
    resetForm();
    resetFormErrors();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCloseModal}
      title={tPropertyPage`editAmenityNameModalTitle` || ''}
      submitButton={{
        title: tCommonLabels`save` || '',
        isLoading: loading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: tCommonLabels`cancel` || '',
        onClick: onCloseModal,
      }}
    >
      <Flex flexDirection="column" mt={3}>
        <AmenityFlexItem p={2} w="100%" alignItems="center">
          <AmenityImage
            fit="scale-down"
            size={56}
            src={payload?.amenity?.imageUrl || ''}
            alt={payload?.amenity?.name}
            mr={3}
          />
          <Text color={mainTheme.colors.n80}>{payload?.amenity?.name}</Text>
        </AmenityFlexItem>
        <Form ref={formRef} onSubmit={onSubmitForm} w="100%" mt={3}>
          <Form.Group w="100%">
            <Form.Label required>{tCommonLabels`name`}</Form.Label>
            <Input
              mt={1}
              w="100%"
              onChange={onNameTextChange}
              $errorType={formErrors[FormFields.Name]?.type}
            />
            <Form.Feedback $errorType={formErrors[FormFields.Name]?.type}>
              {formErrors[FormFields.Name]?.message}
            </Form.Feedback>
          </Form.Group>
        </Form>
      </Flex>
    </Modal>
  );
}

export default EditAmenityNameInPropertyModal;
